import React, { useEffect, useRef, useState } from 'react';
import { View, Text as RNText, StyleSheet, Platform } from 'react-native';

import { Icon } from './icon';
import { IconButton } from './icon-button';

import { useTheme } from '@/components/theme-provider';
import { typography, colors } from '@/styles';

export interface CopyButtonProps {
	text: string;
	size?: number;
}

export const CopyButton: React.FC<CopyButtonProps> = ({ text, size = 16 }) => {
	const theme = useTheme();
	const [copied, setCopied] = useState(false);
	const timeoutRef = useRef<any>(null);

	useEffect(() => {
		return () => {
			if (timeoutRef.current) clearTimeout(timeoutRef.current);
		};
	}, []);

	if (Platform.OS !== 'web' || !text) return null;

	const handleCopy = async () => {
		try {
			if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
				await navigator.clipboard.writeText(text);
			} else {
				const ta = document.createElement('textarea');
				ta.value = text;
				ta.style.position = 'fixed';
				ta.style.opacity = '0';
				document.body.appendChild(ta);
				ta.focus();
				ta.select();
				document.execCommand('copy');
				document.body.removeChild(ta);
			}
			setCopied(true);
			if (timeoutRef.current) clearTimeout(timeoutRef.current);
			timeoutRef.current = setTimeout(() => setCopied(false), 3000);
		} catch {
			setCopied(false);
		}
	};

	if (copied) {
		return (
			<View style={styles.copiedContainer}>
				<Icon name="checkmark-circle" size={size} color={colors.success} />
				<RNText style={[styles.copiedText, { color: colors.success, fontFamily: typography.fontFamily }]}>
					Copied
				</RNText>
			</View>
		);
	}

	return (
		<IconButton
			icon="clipboard-outline"
			size={size}
			onPress={handleCopy}
			iconColor={theme.colors.onSurfaceVariant}
			accessibilityLabel="Copy text"
			style={styles.copyButton}
		/>
	);
};

const styles = StyleSheet.create({
	copiedContainer: {
		flexDirection: 'row',
		alignItems: 'center',
		marginLeft: 6,
	},
	copyButton: {
		marginLeft: 6,
	},
	copiedText: {
		marginLeft: 4,
		fontSize: 12,
		fontWeight: '500',
	},
});
